import { ref, computed } from 'vue';
import { PROVIDERS_ES, PROVIDERS_US, PROVIDERS_UK } from '@/config/searchProviders';
import {
  excludeHiddenMarketplaceProviders,
  stripHiddenMarketplaceProviderIds,
} from '@/utils/marketplaceProviderFilters';
import {
  applyPbsaFiltersIfResidencias,
  isResidenciasHubRoute,
  PBSA_ES_PROVIDER_IDS,
} from '@/utils/residenciasSearch';
import {
  applyAccommodationTypeLandingFilters,
  getAccommodationTypeFromRoute,
} from '@/utils/accommodationTypeLandings';

const priceSlugMapping = {
  // ── España (6 baremos) ──
  'hasta-500': 500,
  'hasta-700': 700,
  'hasta-900': 900,
  'hasta-1200': 1200,
  'hasta-1500': 1500,
  'mas-de-1500': -1500,
  // ── EN slugs (UK + US, sin duplicados) ──
  'up-to-1000': 1000,
  'up-to-1200': 1200,
  'up-to-1500': 1500,
  'up-to-1800': 1800,
  'up-to-2200': 2200,
  'over-2200': -2200,
  'up-to-2500': 2500,
  'up-to-3500': 3500,
  'up-to-5000': 5000,
  'over-5000': -5000,
  // ── Legacy slugs (301 compatibilidad) ──
  baratos: 500,
  economicos: 700,
  'hasta-400': 400,
  'hasta-600': 600,
  'hasta-800': 800,
  'hasta-1000': 1000,
  premium: 1500,
  lujo: 2000,
};

/**
 * Devuelve el valor de precio de un slug de URL.
 * - Positivo = maxPrice (hasta X)
 * - Negativo = minPrice (más de X), valor absoluto es el umbral
 * - null = slug no reconocido
 */
export function getPriceFromSlug(slug) {
  if (!slug) return null;
  return priceSlugMapping[slug.toLowerCase()] ?? null;
}

/** Slugs principales por región (para pills, barras y sitemap). */
export const PRICE_SLUGS_BY_REGION = {
  es: [
    { slug: 'hasta-500', value: 500, label: 'Hasta 500€' },
    { slug: 'hasta-700', value: 700, label: 'Hasta 700€' },
    { slug: 'hasta-900', value: 900, label: 'Hasta 900€' },
    { slug: 'hasta-1200', value: 1200, label: 'Hasta 1.200€' },
    { slug: 'hasta-1500', value: 1500, label: 'Hasta 1.500€' },
    { slug: 'mas-de-1500', value: -1500, label: 'Más de 1.500€' },
  ],
  uk: [
    { slug: 'up-to-1000', value: 1000, label: 'Up to £1,000' },
    { slug: 'up-to-1200', value: 1200, label: 'Up to £1,200' },
    { slug: 'up-to-1500', value: 1500, label: 'Up to £1,500' },
    { slug: 'up-to-1800', value: 1800, label: 'Up to £1,800' },
    { slug: 'up-to-2200', value: 2200, label: 'Up to £2,200' },
    { slug: 'over-2200', value: -2200, label: 'Over £2,200' },
  ],
  us: [
    { slug: 'up-to-1200', value: 1200, label: 'Up to $1,200' },
    { slug: 'up-to-1800', value: 1800, label: 'Up to $1,800' },
    { slug: 'up-to-2500', value: 2500, label: 'Up to $2,500' },
    { slug: 'up-to-3500', value: 3500, label: 'Up to $3,500' },
    { slug: 'up-to-5000', value: 5000, label: 'Up to $5,000' },
    { slug: 'over-5000', value: -5000, label: 'Over $5,000' },
  ],
};

/** Convierte un valor de precio a su slug URL para una región dada. */
export function getSlugFromPrice(priceValue, regionId) {
  if (priceValue == null) return null;
  const region = regionId || 'es';
  const slugs = PRICE_SLUGS_BY_REGION[region] || PRICE_SLUGS_BY_REGION.es;
  const match = slugs.find(s => s.value === priceValue);
  return match?.slug || null;
}

/**
 * Filter state initialization from URL/route.
 */
export function useSearchFiltersSetup(deps) {
  const { route, regionId, cityMapping, availableLocations } = deps;

  function hasActiveCityFilterInUrl() {
    const locationsQuery = route.query.locations;
    if (locationsQuery && typeof locationsQuery === 'string') {
      const parts = locationsQuery.split(',').map((loc) => loc.trim()).filter(Boolean);
      if (parts.length > 0) return true;
    }
    const cityParam = route.params.city;
    return !!(cityParam && cityMapping[cityParam.toLowerCase()]);
  }

  function getDefaultSortBy() {
    return hasActiveCityFilterInUrl() ? 'date_desc' : 'market_relevance';
  }

  function getInitialFilters() {
    return {
      contractType: 'coliving',
      sortBy: getDefaultSortBy(),
      maxPrice: null,
      policies: { pets: false, couples: false },
      maxMinimumStay: null,
      services: { internet: false, cleaning: false, allIncluded: false },
      minBathrooms: null,
      accommodationType: { building: false, apartment: false },
      providers: isResidenciasHubRoute(route.path) ? [...PBSA_ES_PROVIDER_IDS] : [],
      forStudents: route.path.startsWith('/residencias-estudiantes') && !isResidenciasHubRoute(route.path),
      proOnly: false,
    };
  }

  function initSelectedLocations() {
    const locationsQuery = route.query.locations;
    if (locationsQuery && typeof locationsQuery === 'string') {
      const locations = locationsQuery.split(',')
        .map((loc) => loc.trim())
        .filter((loc) => availableLocations.value.includes(loc));
      if (locations.length > 0) return locations;
    }

    const cityParam = route.params.city;
    if (cityParam && cityMapping[cityParam.toLowerCase()]) {
      const mappedCity = cityMapping[cityParam.toLowerCase()];
      // New York / London: propertyLocation en API → grupos metro en searchProperties (NYC_LOCATION_GROUPS, etc.)
      return [mappedCity];
    }
    return [];
  }

  function initMaxPrice() {
    const maxPriceQuery = route.query.maxPrice;
    if (maxPriceQuery) {
      const priceValue = parseInt(maxPriceQuery, 10);
      if (!isNaN(priceValue) && priceValue !== 0) return priceValue;
    }
    const priceSlug = route.params.priceSlug;
    if (priceSlug) return getPriceFromSlug(priceSlug);
    return null;
  }

  const initialFilters = getInitialFilters();
  initialFilters.providers = stripHiddenMarketplaceProviderIds(initialFilters.providers);
  applyPbsaFiltersIfResidencias(route.path, initialFilters);
  applyAccommodationTypeLandingFilters(getAccommodationTypeFromRoute(route), initialFilters);

  const filters = ref({
    ...initialFilters,
    maxPrice: initMaxPrice(),
  });
  const selectedLocations = ref(initSelectedLocations());

  const availableProviders = computed(() => {
    let list = PROVIDERS_ES;
    if (regionId.value === 'us') list = PROVIDERS_US;
    else if (regionId.value === 'uk') list = PROVIDERS_UK;
    return excludeHiddenMarketplaceProviders(list);
  });

  const currentPriceSlug = computed(() => route.params.priceSlug || null);

  return {
    filters,
    selectedLocations,
    availableProviders,
    getInitialFilters,
    getDefaultSortBy,
    getPriceFromSlug,
    currentPriceSlug,
  };
}
